import axios, { AxiosError, AxiosInstance } from 'axios';
import { getApiUrl } from '../config/runtime';
import errorLogger from './errorLogger.service';

// Create component loggers for API and TokenManager
const logger = errorLogger.createComponentLogger('API');
const tokenLogger = errorLogger.createComponentLogger('TokenManager');

// API base URL - will be set dynamically
let API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Create axios instance (will be updated with runtime config)
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS with credentials
  timeout: 30000, // 30 second timeout to prevent infinite hangs
});

// Initialize API with runtime configuration
export const initializeApi = async () => {
  try {
    const apiUrl = await getApiUrl();
    API_BASE_URL = apiUrl;
    api.defaults.baseURL = apiUrl;
    logger.info('Initialized with URL:', apiUrl);
  } catch (error) {
    logger.error('Failed to load runtime config, using default:', API_BASE_URL);
  }
};

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Token management
export const tokenManager = {
  getAccessToken: () => {
    // Try localStorage first
    let token = localStorage.getItem(ACCESS_TOKEN_KEY);
    let source = 'localStorage';
    
    // Fallback to sessionStorage for mobile browsers that might have issues
    if (!token) {
      token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      source = 'sessionStorage';
    }
    // Final fallback to cookies for mobile browsers
    if (!token) {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === 'access_token') {
          token = value;
          source = 'cookie';
          // Re-sync to storage for consistency
          if (token) {
            localStorage.setItem(ACCESS_TOKEN_KEY, token);
            sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
          }
          break;
        }
      }
    }
    
    if (token) {
      tokenLogger.debug('Access token found from:', { source, length: token.length, preview: token.substring(0, 30) + '...' });
    } else {
      tokenLogger.debug('No access token found in any storage');
    }
    
    return token;
  },
  getRefreshToken: () => {
    // Try localStorage first
    let token = localStorage.getItem(REFRESH_TOKEN_KEY);
    // Fallback to sessionStorage
    if (!token) {
      token = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    }
    // Final fallback to cookies for mobile browsers
    if (!token) {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === 'refresh_token') {
          token = value;
          // Re-sync to storage for consistency
          if (token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, token);
            sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
          }
          break;
        }
      }
    }
    return token;
  },
  setTokens: (accessToken: string, refreshToken?: string) => {
    // Store in both localStorage and sessionStorage for mobile compatibility
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    // Also store in cookies for better mobile persistence
    try {
      document.cookie = `access_token=${accessToken}; path=/; max-age=3600; SameSite=Strict`;
      if (refreshToken) {
        document.cookie = `refresh_token=${refreshToken}; path=/; max-age=86400; SameSite=Strict`;
      }
    } catch (e) {
      tokenLogger.warning('[TokenManager] Could not set cookies:', e);
    }
  },
  clearTokens: () => {
    logger.error('clearTokens() called - THIS IS CAUSING LOGOUT', new Error().stack);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    // Also clear cookies
    document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  },
  clearAllAuthData: () => {
    logger.error('clearAllAuthData() called - THIS IS CAUSING LOGOUT', new Error().stack);
    // Clear tokens from both storages
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    // Clear cookies
    document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    // Clear member data
    localStorage.removeItem('member');
    localStorage.removeItem('member_data');
    // Clear any other cached data
    localStorage.removeItem('selectedCustomer');
    localStorage.removeItem('voicemail_last_refresh');
    // Clear all session data
    sessionStorage.clear();
  },
  isTokenExpired: (token: string): boolean => {
    try {
      if (!token || typeof token !== 'string') {
        tokenLogger.error(' Invalid token type:', typeof token);
        return true;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        tokenLogger.error(' Invalid JWT format, parts:', parts.length);
        tokenLogger.debug(' Token preview:', token.substring(0, 50));
        return true;
      }
      
      // Handle URL-safe base64
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      const exp = payload.exp;
      
      if (!exp) return false; // No expiration
      
      // Check if token expires in less than 5 seconds (reduced from 30)
      // This prevents premature refresh during normal usage
      const now = Math.floor(Date.now() / 1000);
      const isExpired = exp < (now + 5);
      
      if (isExpired) {
        tokenLogger.debug(' Token is expired or expiring soon:', {
          exp: new Date(exp * 1000).toISOString(),
          now: new Date(now * 1000).toISOString(),
          secondsUntilExpiry: exp - now
        });
      }
      
      return isExpired;
    } catch (e) {
      tokenLogger.error(' Error checking token expiration:', e);
      tokenLogger.debug(' Problem token:', token ? token.substring(0, 100) : 'null');
      return true; // Assume expired if can't parse
    }
  },
};

// Track request IDs for debugging
let requestCounter = 0;

// Request interceptor to add auth token and check expiration
api.interceptors.request.use(
  async (config) => {
    // Add request ID for tracking
    const requestId = ++requestCounter;
    (config as any).requestId = requestId;
    
    // Don't override Content-Type for FormData (let browser set it with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      logger.debug(' FormData detected, allowing browser to set Content-Type');
    }
    
    // Skip auth for public endpoints
    const publicEndpoints = [
      '/auth/login',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/create-password',
      '/auth/validate-token',
      '/auth/two-factor', // 2FA code sending/verification doesn't require token
      '/endpoints/create-password', // Password creation endpoint - must be public
      '/endpoints/verify-signup-phone', // Phone verification during signup - must be public
      '/endpoints/branding', // Branding is needed on public pages like login
      '/endpoints/config', // Config might be needed on public pages
      '/health' // Health check endpoint
    ];
    
    // Skip logging for error-logs endpoint to prevent infinite loop
    const isErrorLogEndpoint = config.url?.includes('/error-logs') || (window as any).__isLoggingRequest;

    // Debug ALL requests EXCEPT error-logs to prevent infinite loop
    if (!isErrorLogEndpoint) {
      logger.debug(`Request #${requestId}:`, {
        url: config.url,
        method: config.method,
        isBranding: config.url?.includes('branding'),
        isRefreshing: isRefreshing,
        hasToken: !!tokenManager.getAccessToken()
      });
    }

    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

    if (!isErrorLogEndpoint) {
      logger.debug(` Checking auth for ${config.url}:`, {
        isPublic: isPublicEndpoint,
        hasToken: !!tokenManager.getAccessToken()
      });
    }
    
    if (!isPublicEndpoint) {
      const token = tokenManager.getAccessToken();

      console.log('[API Interceptor] Checking endpoint:', config.url, 'Has token:', !!token);

      if (!token) {
        logger.error(' No token available for protected endpoint:', config.url);

        // Don't redirect if we're already on the login page
        if (window.location.pathname === '/login' ||
            window.location.pathname === '/forgot-password' ||
            window.location.pathname === '/reset-password') {
          return Promise.reject(new Error('No authentication token available'));
        }

        // Special handling for phone operations - these should fail gracefully
        const isPhoneOperation = config.url?.includes('/endpoints/phone_numbers');
        if (isPhoneOperation) {
          logger.error(' No token for phone operation - THIS IS THE LOGOUT BUG - failing gracefully without logout:', config.url);
          // THIS IS THE PROBLEM - when the token disappears mid-session, phone operations fail
          // But we don't want to log out the user
          // The token might have been cleared by something else
          return Promise.reject(new Error('Authentication required for phone operation'));
        }

        // Only redirect if this is a critical endpoint and not a background request
        const criticalEndpoints = ['/auth/validate-token', '/auth/refresh'];
        const isCritical = criticalEndpoints.some(endpoint => config.url?.includes(endpoint));

        if (isCritical) {
          logger.error(' No token for critical endpoint, redirecting:', config.url);
          const isMobile = /Mobile|Android|iPhone/i.test(navigator.userAgent);
          if (!isMobile) {
            tokenManager.clearAllAuthData();
            window.location.href = '/login';
          }
        } else {
          tokenLogger.warning('[API] No token for non-critical endpoint:', config.url);
        }
        return Promise.reject(new Error('No authentication token available'));
      }
      
      // Skip ALL token refresh if flag is set (for debugging search issues)
      if ((window as any).__skipTokenRefresh) {
        logger.debug(' Token refresh SKIPPED due to flag');
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
      
      // Check if we're already refreshing - ALL requests should wait
      if (isRefreshing) {
        console.log('[API Interceptor] Already refreshing - queuing request:', config.url);
        logger.debug(' Already refreshing, queuing request');
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken: string) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            resolve(config);
          });
        });
      }

      // Skip token expiry check completely for read-only endpoints
      const readOnlyEndpoints = ['/endpoints/users', '/endpoints/voicemail', '/endpoints/branding', '/endpoints/available_extensions'];
      const isReadOnly = readOnlyEndpoints.some(endpoint => config.url?.includes(endpoint));

      // Only check token expiry for write operations
      const isExpired = !isReadOnly && tokenManager.isTokenExpired(token);
      console.log('[API Interceptor] Token check:', { url: config.url, isReadOnly, isExpired });

      if (isExpired) {
        console.log('[API Interceptor] Token is EXPIRED - will attempt refresh');
        logger.debug(' Token expired or expiring soon for request:', config.url);
        logger.debug(' Request count:', requestCounter);

        // Don't try to refresh for the refresh endpoint itself
        if (config.url?.includes('/auth/refresh')) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          const refreshToken = tokenManager.getRefreshToken();
          console.log('[API Interceptor] Has refresh token:', !!refreshToken);

          if (refreshToken) {
            isRefreshing = true;
            console.log('[API Interceptor] Starting token refresh...');
            logger.debug(' Starting token refresh...');

            try {
              console.log('[API Interceptor] Calling /auth/refresh endpoint...');
              const response = await api.post('/auth/refresh', {
                refresh_token: refreshToken,
              });
              console.log('[API Interceptor] Refresh response received:', response.status);

              const { access_token, refresh_token: newRefreshToken } = response.data.data;
              tokenManager.setTokens(access_token, newRefreshToken);
              config.headers.Authorization = `Bearer ${access_token}`;
              console.log('[API Interceptor] Token refreshed successfully');
              logger.debug(' Token refreshed successfully');

              // Notify all waiting requests
              refreshSubscribers.forEach(callback => callback(access_token));
              refreshSubscribers = [];

              // Reset refreshing flag and return config
              isRefreshing = false;
              return config;
            } catch (error: any) {
              logger.error(' Token refresh failed:', error, false); // Don't show to user

              // Reset refresh state immediately
              isRefreshing = false;

              // Only logout if we get a 401 (unauthorized) from the refresh endpoint
              // Don't logout on network errors or other temporary issues
              if (error.response?.status === 401) {
                logger.error(' Refresh token invalid, logging out', error, false); // Don't show to user
                // Notify waiting requests of failure
                refreshSubscribers.forEach(callback => callback(token)); // Use old token as fallback
                refreshSubscribers = [];

                const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
                if (!isMobile) {
                  // Clear auth on 401 from refresh - tokens are definitely invalid
                  tokenManager.clearAllAuthData();
                  window.location.href = '/login';
                }
              } else {
                tokenLogger.warning('[API] Token refresh failed but not 401, continuing with existing token');
                // Continue with the existing token - it might still work
                config.headers.Authorization = `Bearer ${token}`;

                // Notify waiting requests to use existing token
                refreshSubscribers.forEach(callback => callback(token));
                refreshSubscribers = [];
              }

              // Continue with the original request using existing token
              config.headers.Authorization = `Bearer ${token}`;
              return config;
            }
          } else {
            // No refresh token - this is a definitive auth failure
            tokenLogger.warning('[API] No refresh token available, clearing auth');
            const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
            if (!isMobile) {
              tokenManager.clearAllAuthData();
              window.location.href = '/login';
            }
            return Promise.reject(new Error('No refresh token available'));
          }
        }
      } else {
        // For read-only endpoints or valid tokens, just add the auth header
        console.log('[API Interceptor] Token valid or read-only - adding auth header');
        logger.debug(` Setting auth header for ${config.url}`);
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      logger.debug(` Skipping auth for public endpoint: ${config.url}`);
    }
    
    logger.debug(` Final config for ${config.url}:`, {
      hasAuthHeader: !!config.headers.Authorization
    });

    console.log('[API Interceptor] Request interceptor COMPLETE - sending request to:', config.url);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
// Already declared above: let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    console.log('[API Response Interceptor] SUCCESS:', response.config?.url, 'Status:', response.status);
    return response;
  },
  async (error: AxiosError) => {
    console.log('[API Response Interceptor] ERROR:', error.config?.url, 'Status:', error.response?.status, 'Message:', error.message);
    const originalRequest = error.config as any;

    // Log all 401 errors to debug the search logout issue
    if (error.response?.status === 401) {
      logger.error(' 401 ERROR - LOGOUT TRIGGERED:', {
        url: originalRequest.url,
        method: originalRequest.method,
        pathname: window.location.pathname,
        isRetry: originalRequest._retry,
        hasToken: !!tokenManager.getAccessToken(),
        stack: new Error().stack,
      });
    }


    // Skip refresh attempt for refresh endpoint itself
    if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      logger.error(' 401 on refresh endpoint - clearing auth and redirecting');
      tokenManager.clearAllAuthData();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Don't redirect on 401 if we're already on the login page
    if (window.location.pathname === '/login') {
      return Promise.reject(error);
    }

    // Special handling for phone number search - don't auto-logout
    const isPhoneSearch = originalRequest.url?.includes('/endpoints/phone_numbers') &&
                         (originalRequest.url?.includes('action=search_local_numbers') ||
                          originalRequest.url?.includes('action=get_cities') ||
                          originalRequest.url?.includes('action=get_areas'));
    if (error.response?.status === 401 && isPhoneSearch) {
      logger.error(' 401 on phone search - NOT auto-logging out to prevent race condition', {
        url: originalRequest.url,
        isRetry: originalRequest._retry,
        hasRefreshToken: !!tokenManager.getRefreshToken(),
        hasAccessToken: !!tokenManager.getAccessToken()
      });
      // Don't process this as a normal 401 - just return the error to the component
      // The component can decide how to handle it
      return Promise.reject(error);
    }

    // CRITICAL FIX: Check if this is a concurrent request during refresh
    // If we're refreshing and get ANY auth error, queue the request
    if (error.response?.status === 401 && isRefreshing) {
      logger.debug(' 401 during refresh, queuing request:', originalRequest.url);
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          originalRequest._retry = true; // Mark as retry to prevent loops
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip auth refresh for certain endpoints that might have auth issues
      const skipAuthRefreshEndpoints: string[] = [];
      const shouldSkipRefresh = skipAuthRefreshEndpoints.some(endpoint => 
        originalRequest.url?.includes(endpoint)
      );
      
      if (shouldSkipRefresh) {
        tokenLogger.warning('[API] Skipping auth refresh for endpoint:', originalRequest.url);
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();

      if (!refreshToken) {
        logger.error(' No refresh token available for 401 response');
        isRefreshing = false; // Reset the flag
        processQueue(error, null); // Process any queued requests


        const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
        if (!isMobile) {
          // Add a small delay to prevent race conditions during search
          // Don't auto-redirect here - let the component handle it
          // This prevents race conditions during search
          logger.error(' No token after failed refresh, but not auto-redirecting');
        }
        return Promise.reject(error);
      }

      try {
        logger.debug(' Attempting token refresh for:', originalRequest.url);
        const response = await api.post('/auth/refresh', {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data.data;
        tokenManager.setTokens(access_token, newRefreshToken);
        processQueue(null, access_token);
        logger.debug(' Token refresh successful, retrying original request');

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        logger.error(' Token refresh failed:', refreshError, false); // Don't show to user
        processQueue(refreshError, null);


        // Only clear auth if we get a definitive auth failure (401)
        // Don't clear on network errors or other temporary issues
        if (refreshError.response?.status === 401) {

          const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
          if (!isMobile) {
            // Clear auth and redirect on definitive 401
            logger.error(' 401 on token refresh, clearing auth and redirecting', refreshError, false); // Don't show to user
            tokenManager.clearAllAuthData();
            window.location.href = '/login';
          }
        } else {
          tokenLogger.warning('[API] Token refresh failed with non-401 error, not clearing auth');
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Export function to get the current API base URL
export const getApiBaseUrl = () => API_BASE_URL;

export default api;